import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { dashboardAPI } from '../services/api';
import {
    FileText, Users, TrendingUp, Plus,
    ArrowRight, Calendar,
    PieChart as PieIcon, BarChart3, Wallet, Car
} from 'lucide-react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // Helper to get backend root URL (stripping /api if present)
    const assetBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

    useEffect(() => {
        dashboardAPI.get()
            .then(res => setData(res.data.data))
            .catch(() => toast.error('Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const stats = data?.statistiques || {};
    const tendances = data?.tendances || {};

    // Trend badge helper
    const TrendBadge = ({ pct }) => {
        if (pct === undefined || pct === null) return null;
        const up = pct >= 0;
        return (
            <span style={{
                fontSize: '11px',
                fontWeight: '700',
                padding: '3px 7px',
                borderRadius: '20px',
                background: up ? '#ecfdf5' : '#fef2f2',
                color: up ? '#059669' : '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                whiteSpace: 'nowrap',
            }}>
                {up ? '▲' : '▼'} {Math.abs(pct)}% <span style={{ fontWeight: '400', opacity: 0.7 }}>vs mois préc.</span>
            </span>
        );
    };

    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour < 12) return "Bonjour";
        if (hour < 18) return "Bon après-midi";
        return "Bonsoir";
    }, []);

    const formatCurrency = (val) => {
        if (!val && val !== 0) return '0,00 DA';
        return new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val) + ' DA';
    }

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-DZ') : '—';

    // Data for Charts
    const sinistreStats = [
        { name: 'Brouillons', value: stats.sinistres_brouillon || 0, color: '#94a3b8' },
        { name: 'En cours', value: stats.sinistres_en_cours || 0, color: '#3b82f6' },
        { name: 'Terminés', value: stats.rapports_finalises || 0, color: '#10b981' },
    ];

    const financialStats = [
        { name: 'Émis', value: stats.ca_emis || 0 },
        { name: 'Encaissé', value: stats.ca_total || 0 },
    ];

    const headerActions = (
        <button
            className="btn btn-primary btn-circle"
            onClick={() => navigate('/sinistres/rapide')}
            style={{
                borderRadius: '50% !important',
                width: '44px',
                height: '44px',
                minWidth: '44px',
                maxWidth: '44px',
                flexShrink: 0,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(26,58,92,0.25)',
                border: 'none',
                lineHeight: 1
            }}
            title="Nouveau Dossier Rapide"
        >
            <Plus size={24} strokeWidth={3} />
        </button>
    );

    // ── Unified Activity List (Aggregating New Data Types) ─────────────────
    const mergedActivities = useMemo(() => {
        if (!data) return [];
        
        const act = [];
        
        // Add Sinistres
        (data.derniers_sinistres || []).forEach(s => {
            act.push({
                ...s,
                unified_type: 'sinistre',
                unified_title: s.nom_assure || 'Client Inconnu',
                unified_subtitle: `#${s.numero_sinistre}${s.immatriculation ? ' · ' + s.immatriculation : ''}`,
                unified_icon: <Users size={18} />,
                unified_date: s.created_at,
                unified_link: `/sinistres/${s.id}`
            });
        });

        // Add Rapports
        (data.derniers_rapports || []).forEach(r => {
            act.push({
                ...r,
                unified_type: 'rapport',
                unified_title: `Rapport ${r.numero_rapport}`,
                unified_subtitle: r.total_general ? `Montant: ${formatCurrency(r.total_general)}` : 'Nouveau Rapport',
                unified_icon: <FileText size={18} />,
                unified_date: r.created_at,
                unified_link: `/rapports/${r.id}`
            });
        });

        // Add Honoraires
        (data.derniers_honoraires || []).forEach(h => {
            act.push({
                ...h,
                unified_type: 'honoraire',
                unified_title: `Note d'Honoraire ${h.numero_honoraire}`,
                unified_subtitle: `Montant: ${formatCurrency(h.montant_ttc)}`,
                unified_icon: <Wallet size={18} />,
                unified_date: h.created_at,
                unified_link: `/honoraires/${h.id}`
            });
        });

        // Sort by date (desc) and keep top 8
        return act
            .filter(item => item.unified_date) // Ensure date exists
            .sort((a, b) => new Date(b.unified_date) - new Date(a.unified_date))
            .slice(0, 8);
    }, [data]);

    if (loading) return (
        <Layout title="Tableau de bord">
            <div className="loading-spinner"><div className="spinner" /></div>
        </Layout>
    );

    return (
        <Layout title="Tableau de bord" headerActions={headerActions}>
            <div className="animate-fade-in" style={{ animation: 'fadeIn 0.5s ease-out' }}>

                {/* Welcome Section */}
                <div className="card premium-welcome-card" style={{ 
                    marginBottom: '30px',
                    padding: '24px',
                    color: 'white',
                    boxShadow: 'var(--shadow-lg)'
                }}>
                    <div className="welcome-shimmer" />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '4px', display: 'flex', alignItems: 'center' }}>
                            {greeting}, {user?.nom || user?.username} 
                            <span className="pulse-sparkle" style={{ marginLeft: '10px' }}>✦</span>
                        </h2>
                        <p style={{ opacity: 0.8, fontSize: '14px' }}>
                            Voici un aperçu de votre activité d'expertise pour aujourd'hui.
                        </p>
                    </div>
                </div>

                {/* Hero Stats Card Grid */}
                <div className="stats-grid" style={{ marginBottom: '30px' }}>
                    <div className="stat-card blue">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <div className="stat-icon blue"><Car size={20} /></div>
                            <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--primary-600)', background: 'rgba(37, 99, 168, 0.12)', padding: '4px 8px', borderRadius: '20px' }}>Global</span>
                        </div>
                        <div className="stat-number">{stats.total_sinistres || 0}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                            <div className="stat-label">Dossiers Sinistres</div>
                            <TrendBadge pct={tendances.sinistres_pct} />
                        </div>
                        {tendances.sinistres_ce_mois !== undefined && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                {tendances.sinistres_ce_mois} ce mois · {tendances.sinistres_mois_precedent} le mois dernier
                            </div>
                        )}
                    </div>
                    <div className="stat-card gold">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <div className="stat-icon gold"><FileText size={20} /></div>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--gold-500)', background: 'var(--gold-100)', padding: '4px 8px', borderRadius: '20px' }}>Expertise</span>
                        </div>
                        <div className="stat-number">{stats.total_rapports || 0}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                            <div className="stat-label">Rapports PV</div>
                            <TrendBadge pct={tendances.rapports_pct} />
                        </div>
                        {tendances.rapports_ce_mois !== undefined && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                {tendances.rapports_ce_mois} ce mois · {tendances.rapports_mois_precedent} le mois dernier
                            </div>
                        )}
                    </div>
                    <div className="stat-card green">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <div className="stat-icon green"><Wallet size={20} /></div>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--success)', background: '#ecfdf5', padding: '4px 8px', borderRadius: '20px' }}>Gestion</span>
                        </div>
                        <div className="stat-number">{stats.total_honoraires || 0}</div>
                        <div className="stat-label">Notes d'honoraires</div>
                    </div>
                    <div className="stat-card purple">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <div className="stat-icon purple"><TrendingUp size={20} /></div>
                            <span style={{ fontSize: '11px', fontWeight: '800', color: '#7c3aed', background: '#f5f3ff', padding: '4px 8px', borderRadius: '20px' }}>Finances</span>
                        </div>
                        <div className="stat-number" style={{ fontSize: '20px', color: 'var(--primary-700)' }}>{formatCurrency(stats.ca_total)}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                            <div className="stat-label" style={{ color: 'var(--primary-600)' }}>CA Encaissé</div>
                            <TrendBadge pct={tendances.ca_pct} />
                        </div>
                        {tendances.ca_ce_mois !== undefined && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                Ce mois&nbsp;: {formatCurrency(tendances.ca_ce_mois)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Charts Area */}
                <div className="grid-2" style={{ marginBottom: '30px', gap: '24px' }}>
                    <div className="card" style={{ padding: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            <div style={{ background: 'var(--primary-50)', p: '8px', borderRadius: '8px', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                                <PieIcon size={18} />
                            </div>
                            <h3 className="card-title" style={{ margin: 0 }}>État des Dossiers</h3>
                        </div>
                        <div style={{ height: '250px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={sinistreStats.filter(s => s.value > 0)}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {sinistreStats.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: 'var(--shadow-lg)' }}
                                    />
                                    <Legend verticalAlign="bottom" height={36} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="card" style={{ padding: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            <div style={{ background: 'var(--gold-100)', p: '8px', borderRadius: '8px', color: 'var(--gold-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                                <BarChart3 size={18} />
                            </div>
                            <h3 className="card-title" style={{ margin: 0 }}>Récapitulatif Financier</h3>
                        </div>
                        <div style={{ height: '250px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={financialStats} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                    <YAxis hide />
                                    <Tooltip
                                        cursor={{ fill: 'var(--surface-2)' }}
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: 'var(--shadow-lg)' }}
                                        formatter={(val) => formatCurrency(val)}
                                    />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                        {financialStats.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={index === 0 ? 'var(--primary-400)' : 'var(--success)'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* --- VITAMINE SECTION (New Futuristic Widgets) --- */}
                {data?.vitamin && (
                    <div className="grid-3" style={{ marginBottom: '30px', gap: '24px', display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr' }}>
                              {/* 1. Brand Leaderboard */}
                        <div className="card gold-glow" style={{ padding: '24px', position: 'relative' }}>
                            <div className="badge-gold" style={{ position: 'absolute', top: '20px', right: '20px' }}>Top Marque</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                                {data.vitamin.top_brands[0]?.logo ? (
                                    <img 
                                        src={`${assetBaseUrl}/uploads/car_logos/${data.vitamin.top_brands[0].logo}`} 
                                        alt="Logo" 
                                        style={{ width: '50px', height: '50px', objectFit: 'contain' }} 
                                    />
                                ) : (
                                    <div className="stat-icon gold"><Car size={24} /></div>
                                )}
                                <div>
                                    <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Leaderboard</h4>
                                    <h3 style={{ fontSize: '24px', fontWeight: '900', color: 'var(--primary-800)' }}>{data.vitamin.top_brands[0]?.brand || '—'}</h3>
                                </div>
                            </div>
                            
                            <div className="brand-radar" style={{ padding: '15px 0' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '10px' }}>Modèles les plus percutés :</div>
                                {data.vitamin.top_models.slice(0, 3).map((m, i) => (
                                    <div key={i} className="rank-item">
                                        <div className="rank-number">{i + 1}</div>
                                        <div style={{ fontSize: '14px', fontWeight: '600' }}>{m.model}</div>
                                        <div className="rank-count">{m.count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 2. Recurring Risk (Clients) */}
                        <div className="card" style={{ padding: '24px' }}>
                            <h3 className="card-title" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Users size={18} color="var(--primary-600)" /> Dossiers Récurrents
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {data.vitamin.recurring_clients.length > 0 ? (
                                    data.vitamin.recurring_clients.map((c, i) => (
                                        <div key={i} style={{ 
                                            padding: '12px', 
                                            background: 'var(--surface-2)', 
                                            borderRadius: '12px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between' 
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div className="user-avatar" style={{ width: '28px', height: '28px', fontSize: '10px' }}>{c.name.substring(0, 2)}</div>
                                                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                                            </div>
                                            <div style={{ fontSize: '11px', fontWeight: '800', padding: '2px 8px', background: 'var(--primary-50)', color: 'var(--primary-600)', borderRadius: '8px' }}>
                                                {c.count} accidents
                                            </div>
                                        </div>
                                    ))
                                ) : <div className="empty-state">Aucune récurrence détectée</div>}
                            </div>
                        </div>

                        {/* 3. Intelligent Insights */}
                        <div className="card insight-card" style={{ padding: '24px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gold-300)' }}>
                                <TrendingUp size={18} /> IA Curiosités
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {data.vitamin.fun_facts.map((fact, i) => (
                                    <div key={i} style={{ 
                                        fontSize: '13px', 
                                        lineHeight: '1.5', 
                                        padding: '12px', 
                                        background: 'rgba(255,255,255,0.05)', 
                                        borderRadius: '12px',
                                        borderLeft: '3px solid var(--gold-400)',
                                        animation: `fadeIn 0.5s ease-out ${i * 0.2}s both`
                                    }}>
                                        {fact.split('**').map((part, index) => 
                                            index % 2 === 1 ? <strong key={index} style={{ color: 'var(--gold-300)' }}>{part}</strong> : part
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                )}

                {/* --- INSURANCE WORKLOAD --- */}
                {data?.vitamin?.insurance_ranking && (
                    <div className="card" style={{ marginBottom: '30px', padding: '24px' }}>
                         <h3 className="card-title" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <BarChart3 size={18} color="var(--primary-600)" /> Analyse des Partenaires Assurances
                        </h3>
                        <div className="grid-2" style={{ gap: '40px' }}>
                            <div>
                                <h4 style={{ fontSize: '13px', color: 'var(--success)', fontWeight: '800', marginBottom: '15px', textTransform: 'uppercase' }}>🚀 Les plus actifs</h4>
                                <table className="compact-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>#</th>
                                            <th style={{ width: '40px' }}></th>
                                            <th>Assurance</th>
                                            <th style={{ textAlign: 'right' }}>Missions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.vitamin.insurance_ranking.top.map((ins, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="rank-number" style={{ background: 'var(--success)', color: 'white' }}>{i + 1}</div>
                                                </td>
                                                <td>
                                                    {ins.logo ? (
                                                        <img 
                                                            src={`${assetBaseUrl}/assurances_logo/${ins.logo}`} 
                                                            alt="Logo" 
                                                            style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }} 
                                                        />
                                                    ) : (
                                                        <div style={{ width: '28px', height: '28px', background: 'var(--surface-3)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Users size={12} color="var(--text-muted)" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: '600', fontSize: '13px' }}>{ins.name}</span>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <span style={{ color: 'var(--success)', fontWeight: '700', fontSize: '13px' }}>{ins.count}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <h4 style={{ fontSize: '13px', color: 'var(--danger)', fontWeight: '800', marginBottom: '15px', textTransform: 'uppercase' }}>🐢 Les moins actifs</h4>
                                <table className="compact-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>#</th>
                                            <th style={{ width: '40px' }}></th>
                                            <th>Assurance</th>
                                            <th style={{ textAlign: 'right' }}>Missions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.vitamin.insurance_ranking.bottom.map((ins, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="rank-number" style={{ background: 'var(--danger)', color: 'white' }}>{i + 1}</div>
                                                </td>
                                                <td>
                                                    {ins.logo ? (
                                                        <img 
                                                            src={`${assetBaseUrl}/assurances_logo/${ins.logo}`} 
                                                            alt="Logo" 
                                                            style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }} 
                                                        />
                                                    ) : (
                                                        <div style={{ width: '28px', height: '28px', background: 'var(--surface-3)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Users size={12} color="var(--text-muted)" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: '600', fontSize: '13px' }}>{ins.name}</span>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <span style={{ color: 'var(--danger)', fontWeight: '700', fontSize: '13px' }}>{ins.count}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}






                {/* Recent Activities Section */}
                <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Calendar size={18} color="var(--primary-600)" />
                            <h3 className="card-title" style={{ margin: 0 }}>Activités Récentes</h3>
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/sinistres')}>
                            Voir tout <ArrowRight size={14} />
                        </button>
                    </div>

                    <div style={{ padding: '8px 0' }}>
                        {mergedActivities.length === 0 ? (
                            <div className="empty-state">Aucune activité récente</div>
                        ) : (
                            mergedActivities.map((a, idx) => (
                                <div
                                    key={`${a.unified_type}-${a.id}`}
                                    className="table-row-hover"
                                    onClick={() => navigate(a.unified_link)}
                                    style={{
                                        padding: '16px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        cursor: 'pointer',
                                        borderBottom: idx === (mergedActivities.length - 1) ? 'none' : '1px solid var(--surface-2)'
                                    }}
                                >
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        background: a.unified_type === 'sinistre' ? 'rgba(37, 99, 168, 0.1)' : a.unified_type === 'rapport' ? 'var(--gold-100)' : '#f5f3ff',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: a.unified_type === 'sinistre' ? 'var(--primary-600)' : a.unified_type === 'rapport' ? 'var(--gold-500)' : '#7c3aed'
                                    }}>
                                        {a.unified_icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                            <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)' }}>{a.unified_title}</span>
                                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatDate(a.unified_date)}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '12px', color: a.unified_type === 'sinistre' ? 'var(--primary-500)' : a.unified_type === 'rapport' ? 'var(--gold-500)' : '#7c3aed', fontWeight: '600' }}>
                                                {a.unified_subtitle}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        background: a.statut === 'termine' || a.statut === 'pdf_genere' || a.statut === 'paye' ? '#ecfdf5' : a.statut === 'en_cours' ? '#eff6ff' : '#f8fafc',
                                        color: a.statut === 'termine' || a.statut === 'pdf_genere' || a.statut === 'paye' ? '#10b981' : a.statut === 'en_cours' ? '#3b82f6' : '#64748b'
                                    }}>
                                        {a.statut === 'termine' || a.statut === 'pdf_genere' || a.statut === 'paye' ? '✓ Finalisé' : a.statut === 'en_cours' ? '⌛ En cours' : '📝 Brouillon'}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

            </div>
        </Layout>
    );
};

export default Dashboard;
